import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Megaphone, Users, Gift, Crown, QrCode, Link2, Percent, 
  DollarSign, Calendar, TrendingUp, Star, Award, CheckCircle,
  Target, BarChart3, Sparkles, Info, Zap
} from 'lucide-react';

// Move helper components outside the main component
const FeatureCard = ({ icon: Icon, title, description, colorClass = 'teal' }) => (
  <div className={`p-4 rounded-lg border bg-${colorClass}-50 border-${colorClass}-200`}>
    <div className="flex items-start gap-3">
      <div className={`p-2 rounded-lg bg-${colorClass}-100`}>
        <Icon className={`h-5 w-5 text-${colorClass}-600`} />
      </div>
      <div>
        <h4 className="font-semibold text-gray-900">{title}</h4>
        <p className="text-sm text-gray-600 mt-1">{description}</p>
      </div>
    </div>
  </div>
);

const StepCard = ({ number, title, description }) => (
  <div className="flex gap-4 items-start">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold text-sm">
      {number}
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">{title}</h4>
      <p className="text-sm text-gray-600 mt-1">{description}</p>
    </div>
  </div>
);

const TipBox = ({ children, type = 'info' }) => {
  const styles = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    pro: 'bg-purple-50 border-purple-200 text-purple-800'
  };
  const icons = {
    info: Info,
    success: CheckCircle,
    warning: Info,
    pro: Zap
  };
  const IconComponent = icons[type];
  
  return (
    <div className={`p-4 rounded-lg border ${styles[type]} flex gap-3`}>
      <IconComponent className="h-5 w-5 flex-shrink-0 mt-0.5" />
      <div className="text-sm">{children}</div>
    </div>
  );
};

const MarketingGuide = () => {
  const [activeSection, setActiveSection] = useState('overview');

  return (
    <div className="space-y-6" data-testid="marketing-guide">
      {/* Header */}
      <div className="text-center pb-6 border-b">
        <div className="inline-flex items-center justify-center p-3 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl mb-4">
          <Megaphone className="h-8 w-8 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900">Marketing Guide</h1>
        <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
          Learn how to grow your membership base using referrals, promotions, and loyalty rewards
        </p>
      </div>

      {/* Quick Navigation */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { id: 'overview', icon: BarChart3, label: 'Overview', colorClass: 'teal' },
          { id: 'referrals', icon: Users, label: 'Referrals', colorClass: 'blue' },
          { id: 'promos', icon: Percent, label: 'Promo Codes', colorClass: 'purple' },
          { id: 'loyalty', icon: Crown, label: 'Loyalty', colorClass: 'amber' }
        ].map(({ id, icon: Icon, label, colorClass }) => (
          <button
            key={id}
            onClick={() => setActiveSection(id)}
            className={`p-4 rounded-lg border-2 transition-all ${
              activeSection === id 
                ? `border-${colorClass}-500 bg-${colorClass}-50` 
                : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
          >
            <Icon className={`h-6 w-6 mx-auto mb-2 ${activeSection === id ? `text-${colorClass}-600` : 'text-gray-400'}`} />
            <span className={`text-sm font-medium ${activeSection === id ? `text-${colorClass}-700` : 'text-gray-600'}`}>
              {label}
            </span>
          </button>
        ))}
      </div>

      {/* Content Sections */}
      <div className="space-y-6">
        {/* Overview Section */}
        {activeSection === 'overview' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-teal-600" />
                  Marketing Hub Overview
                </CardTitle>
                <CardDescription>
                  Your central dashboard for all marketing activities
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-gray-700">
                  The Marketing Hub is your command center for growing your clinic&apos;s membership base. 
                  Access it from the sidebar under <Badge variant="outline">Marketing</Badge>.
                </p>

                <div className="grid md:grid-cols-3 gap-4">
                  <FeatureCard
                    icon={Users}
                    title="Referral Program"
                    description="Turn happy members into brand ambassadors with unique referral codes"
                    colorClass="blue"
                  />
                  <FeatureCard
                    icon={Percent}
                    title="Promo Codes"
                    description="Create discounts and special offers to attract new members"
                    colorClass="purple"
                  />
                  <FeatureCard
                    icon={Crown}
                    title="Loyalty Program"
                    description="Reward members for engagement with points and exclusive perks"
                    colorClass="amber"
                  />
                </div>

                <TipBox type="pro">
                  <strong>Pro Tip:</strong> Combine all three marketing tools for maximum impact. 
                  Referrers can earn loyalty points, and new members can use promo codes during signup!
                </TipBox>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Dashboard Metrics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { label: 'Active Referrers', desc: 'Members sharing your clinic' },
                      { label: 'Referrals This Month', desc: 'New signups from referrals' },
                      { label: 'Active Promos', desc: 'Currently valid promo codes' },
                      { label: 'Promo Uses', desc: 'Total promo redemptions' }
                    ].map((metric, i) => (
                      <div key={i} className="p-3 bg-gray-50 rounded-lg">
                        <p className="font-medium text-gray-900">{metric.label}</p>
                        <p className="text-xs text-gray-500">{metric.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Referrals Section */}
        {activeSection === 'referrals' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  Referral Program Guide
                </CardTitle>
                <CardDescription>
                  Grow your membership through word-of-mouth marketing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-2">How Referrals Work</h3>
                  <p className="text-blue-800 text-sm">
                    Each admin gets a unique referral code and link. When someone signs up using your 
                    referral link and purchases a membership, both parties receive rewards!
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Getting Started</h3>
                  <div className="space-y-4">
                    <StepCard 
                      number={1}
                      title="Find Your Referral Link"
                      description="Go to Marketing → Referral Program. Your unique link and code will be displayed at the top."
                    />
                    <StepCard 
                      number={2}
                      title="Share Your Link"
                      description="Copy the link or download the QR code. Share via email, social media, or print materials."
                    />
                    <StepCard 
                      number={3}
                      title="Track Performance"
                      description="Monitor successful referrals, pending signups, and your earnings in the dashboard."
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="border-green-200 bg-green-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-green-800">Friend Gets</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-green-600">10% OFF</div>
                      <p className="text-sm text-green-700">On their first membership</p>
                    </CardContent>
                  </Card>
                  <Card className="border-blue-200 bg-blue-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-blue-800">You Get</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-blue-600">10% Reward</div>
                      <p className="text-sm text-blue-700">When they complete payment</p>
                    </CardContent>
                  </Card>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Sharing Options</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-3 border rounded-lg">
                      <Link2 className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="font-medium">Direct Link</p>
                        <p className="text-sm text-gray-500">yoursite.com/join/clinic?ref=CODE</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 border rounded-lg">
                      <QrCode className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="font-medium">QR Code</p>
                        <p className="text-sm text-gray-500">Download and print for in-clinic display</p>
                      </div>
                    </div>
                  </div>
                </div>

                <TipBox type="success">
                  <strong>Best Practice:</strong> Display the QR code at your reception desk and include 
                  the referral link in your email signatures for passive referral generation.
                </TipBox>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Promo Codes Section */}
        {activeSection === 'promos' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Percent className="h-5 w-5 text-purple-600" />
                  Promo Codes Guide
                </CardTitle>
                <CardDescription>
                  Create compelling offers to attract new members
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Discount Types</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-4 border rounded-lg text-center">
                      <Percent className="h-8 w-8 mx-auto mb-2 text-purple-500" />
                      <h4 className="font-semibold">Percentage Off</h4>
                      <p className="text-sm text-gray-500">e.g., 25% off first month</p>
                      <Badge className="mt-2" variant="secondary">Most Popular</Badge>
                    </div>
                    <div className="p-4 border rounded-lg text-center">
                      <DollarSign className="h-8 w-8 mx-auto mb-2 text-green-500" />
                      <h4 className="font-semibold">Fixed Amount</h4>
                      <p className="text-sm text-gray-500">e.g., $50 off any plan</p>
                    </div>
                    <div className="p-4 border rounded-lg text-center">
                      <Calendar className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                      <h4 className="font-semibold">Free Month(s)</h4>
                      <p className="text-sm text-gray-500">e.g., First month free</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Creating a Promo Code</h3>
                  <div className="space-y-4">
                    <StepCard 
                      number={1}
                      title="Go to Marketing → Promo Codes"
                      description="Click the &apos;Create Promo Code&apos; button to open the creation form."
                    />
                    <StepCard 
                      number={2}
                      title="Set Code Details"
                      description="Enter a memorable code (e.g., SUMMER25), select discount type, and set the value."
                    />
                    <StepCard 
                      number={3}
                      title="Configure Limits"
                      description="Set total uses, per-user limit, and expiration date to control your offer."
                    />
                    <StepCard 
                      number={4}
                      title="Activate and Share"
                      description="Save the code and share it through your marketing channels."
                    />
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Quick Templates</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Use these pre-built templates to quickly create common promotions:
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      { name: 'Welcome Discount', value: '15% OFF', colorClass: 'teal' },
                      { name: 'Summer Special', value: '25% OFF', colorClass: 'orange' },
                      { name: 'Flash Sale', value: '50% OFF', colorClass: 'red' },
                      { name: 'Free First Month', value: '1 Month Free', colorClass: 'blue' },
                      { name: 'Loyalty Reward', value: '20% OFF', colorClass: 'purple' },
                      { name: 'Fixed Amount', value: '$50 OFF', colorClass: 'green' }
                    ].map((template, i) => (
                      <div key={i} className={`p-3 rounded-lg border bg-${template.colorClass}-50 border-${template.colorClass}-200`}>
                        <p className="font-medium text-sm">{template.name}</p>
                        <p className={`text-lg font-bold text-${template.colorClass}-600`}>{template.value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <TipBox type="warning">
                  <strong>Important:</strong> Always set an expiration date for promotional codes. 
                  Open-ended promos can lead to revenue loss and reduced urgency for potential members.
                </TipBox>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Loyalty Section */}
        {activeSection === 'loyalty' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-amber-600" />
                  Loyalty Program Guide
                </CardTitle>
                <CardDescription>
                  Reward member engagement with points and exclusive perks
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
                  <h3 className="font-semibold text-amber-900 mb-2">Program Overview</h3>
                  <p className="text-amber-800 text-sm">
                    Members earn points for various activities like using benefits, referring friends, 
                    and renewing memberships. Points can be redeemed for rewards and unlock higher tiers 
                    with better perks.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">How Members Earn Points</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { action: 'Benefit Usage', points: '+50' },
                      { action: 'Referral Completed', points: '+200' },
                      { action: 'Membership Renewal', points: '+100' },
                      { action: 'Check-in', points: '+10' },
                      { action: 'Profile Complete', points: '+50' },
                      { action: 'First Purchase', points: '+100' },
                      { action: 'Review Submitted', points: '+25' },
                      { action: 'Appointment Kept', points: '+30' }
                    ].map((item, i) => (
                      <div key={i} className="p-3 bg-gray-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">{item.action}</p>
                        <p className="text-lg font-bold text-green-600">{item.points}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Membership Tiers</h3>
                  <div className="space-y-3">
                    {[
                      { name: 'Bronze', points: '0+', colorClass: 'amber', benefits: ['Basic rewards access'] },
                      { name: 'Silver', points: '500+', colorClass: 'gray', benefits: ['5% extra discount', 'Priority support'] },
                      { name: 'Gold', points: '1,500+', colorClass: 'yellow', benefits: ['10% extra discount', 'Priority booking', 'Exclusive offers'] },
                      { name: 'Platinum', points: '3,000+', colorClass: 'slate', benefits: ['15% extra discount', 'VIP support', 'Free upgrades', 'Early access'] }
                    ].map((tier, i) => (
                      <div key={i} className={`p-4 rounded-lg border bg-${tier.colorClass}-50 border-${tier.colorClass}-200`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Star className={`h-5 w-5 text-${tier.colorClass}-600`} />
                            <span className="font-semibold">{tier.name}</span>
                          </div>
                          <Badge variant="outline">{tier.points} points</Badge>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {tier.benefits.map((benefit, j) => (
                            <span key={j} className="text-xs bg-white px-2 py-1 rounded border">
                              {benefit}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Tabs defaultValue="admin" className="mt-6">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="admin">For Admins</TabsTrigger>
                    <TabsTrigger value="patient">For Patients</TabsTrigger>
                  </TabsList>
                  <TabsContent value="admin" className="space-y-4 mt-4">
                    <h4 className="font-semibold">Admin Features</h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                        <div>
                          <p className="font-medium">Toggle Program On/Off</p>
                          <p className="text-sm text-gray-500">Enable or disable the loyalty program for your clinic</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <Gift className="h-5 w-5 text-purple-500 mt-0.5" />
                        <div>
                          <p className="font-medium">Create Rewards</p>
                          <p className="text-sm text-gray-500">Set up discounts, free services, or upgrades that members can redeem</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <Sparkles className="h-5 w-5 text-amber-500 mt-0.5" />
                        <div>
                          <p className="font-medium">Award Points Manually</p>
                          <p className="text-sm text-gray-500">Give bonus points to members for special occasions</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <BarChart3 className="h-5 w-5 text-blue-500 mt-0.5" />
                        <div>
                          <p className="font-medium">View Analytics</p>
                          <p className="text-sm text-gray-500">Track total points issued, redemptions, and tier distribution</p>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                  <TabsContent value="patient" className="space-y-4 mt-4">
                    <h4 className="font-semibold">Patient Features</h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <Crown className="h-5 w-5 text-amber-500 mt-0.5" />
                        <div>
                          <p className="font-medium">View Points and Tier</p>
                          <p className="text-sm text-gray-500">See current balance, tier status, and progress to next tier</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <Gift className="h-5 w-5 text-purple-500 mt-0.5" />
                        <div>
                          <p className="font-medium">Browse and Redeem Rewards</p>
                          <p className="text-sm text-gray-500">View available rewards and redeem points for discounts</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <TrendingUp className="h-5 w-5 text-green-500 mt-0.5" />
                        <div>
                          <p className="font-medium">Track Activity</p>
                          <p className="text-sm text-gray-500">View points history and understand how to earn more</p>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>

                <TipBox type="pro">
                  <strong>Engagement Tip:</strong> Set up a Birthday Bonus reward that automatically 
                  gives members extra points on their birthday. It is a personal touch that drives loyalty!
                </TipBox>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <Card className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold">Ready to grow your membership?</h3>
              <p className="text-teal-100">Start using the Marketing Hub today!</p>
            </div>
            <Button variant="secondary" size="lg" className="whitespace-nowrap">
              <Target className="h-4 w-4 mr-2" />
              Go to Marketing Hub
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MarketingGuide;
